import { EMPLOYEE_STAFF_POLICY } from "app_config";
import React, { useEffect, useMemo, useState } from "react";
import { Button, IconButton, ToggleSwitch } from "components/common"
import { useNavigate } from "react-router-dom";
import Select from 'react-select';
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { createEmployee, getPositionGroups, updateEmployee } from 'redux/organization/employeeSlice';
import AsyncSelect from 'react-select/async';
import { debounce } from "helpers/debounce"
import MaskedInput from "react-text-mask";
import NewSiteModal from "./NewSiteModal";
import NewDepartmentModal from "./NewDepartmentModal";
import { useForm, Controller } from "react-hook-form";
import EmployeeSelectField from "components/common/select/EmployeeSelectField";
import { activeEmployeesEnrichedDebounce, buildDepartmentOptions, buildDepartmentLabel } from "helpers/employeeOptionHelper";
import apiAxios from "helpers/apiAxios";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { FaInfoCircle, FaArrowLeft, FaPlus } from "react-icons/fa";

// react-select renders its menu inline, so inside the edit Drawer (or any
// scrolling container) the list is clipped. Portalling to the body fixes it; the
// z-index has to clear the Drawer/Modal layer at 100000.
const SELECT_MENU_PORTAL = { menuPortal: (base) => ({ ...base, zIndex: 100001 }) };

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
import { isSalesTeamTitle } from 'helpers/employeeAccessProfile';
/**
 * `embedded` lets this same form run inside a Drawer (the org chart edits an
 * employee without leaving the chart) instead of as a full page. When embedded,
 * saving and cancelling call back to the host rather than navigating — a route
 * change would throw away the chart's zoom, expansion and selection.
 */
const EmployeeForm = ({ title, employee, embedded = false, onSaved, onCancel }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues } = useForm();
  const { positionGroups } = useSelector((state) => state.organization_employee);

  const syncFullName = () => {
    const first = getValues('first_name') || '';
    const last = getValues('last_name') || '';
    const full = [first, last].filter(Boolean).join(' ');
    if (full) setValue('full_name', full);
  };
  const syncSalesTeam = (event) => {
    setValue('is_sales', isSalesTeamTitle(event.target.value));
  };
  const [showNewSiteModal, setShowNewSiteModal] = useState(false);
  const [showNewDepartmentModal, setShowNewDepartmentModal] = useState(false);
  const [currentSite, setCurrentSite] = useState(employee && employee.site_id);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (employee) {
      Object.entries(employee).forEach(([key, value]) => {
        switch (key) {
          case "staff_policy":
            setValue(key, value?.toLowerCase());
            break;
          case "start_date":
            setValue(key, value ? new Date(value) : null);
            break;
          default:
            setValue(key, value);
            break;
        }
      });
    }
  }, [employee, setValue]);

  const staffPolicyOptions = useMemo(() => EMPLOYEE_STAFF_POLICY.map((option) => ({ value: option.toLocaleLowerCase(), label: option === "SALES" ? "Sales-Office" : option })), []);
  const defaultSiteOption = useMemo(() => (employee && employee.site ? { value: employee.site.id, label: employee.site.name } : null), [employee]);
  const defaultDepartmentOption = useMemo(() => (employee && employee.department ? { value: employee.department.id, label: employee.department.name } : null), [employee]);
  // Prefer `manager` over `parent`: EmployeeDetailSerializer expands it with
  // full_name/email/title/site, so the already-selected manager renders as a
  // person rather than a bare code. `parent` is the {id, name} fallback from the
  // list serializer.
  const defaultManagerOption = useMemo(() => {
    const current = employee?.manager || employee?.parent;
    if (!current?.id) return null;

    return {
      value: current.id,
      label: current.name,
      full_name: current.full_name,
      email: current.email,
    };
  }, [employee]);

  const [departmentOption, setDepartmentOption] = useState(defaultDepartmentOption);

  useEffect(() => {
    dispatch(getPositionGroups());
  }, [dispatch]);

  const onSubmit = async (data) => {
    try {
      if (employee) {
        const employeeId = employee.id;
        await dispatch(updateEmployee({ employeeId, data })).unwrap();
        if (embedded) onSaved?.(employeeId);
        else redirectEmployeeDetail(employeeId);
      } else {
        const response = await dispatch(createEmployee(data));
        const payload = response.payload;
        if (payload !== undefined) {
          if (embedded) onSaved?.(payload.data.id);
          else redirectEmployeeDetail(payload.data.id);
        }
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCancel = () => {
    if (embedded) onCancel?.();
    else navigate(-1);
  };

  const debouncedFilterSites = debounce((inputValue, callback) => {
    filterIndexOptions("/organization/sites", inputValue).then((filteredOptions) => {
      callback(filteredOptions);
    });
  }, 300);

  const loadSiteOptions = (inputValue, callback) => {
    debouncedFilterSites(inputValue, callback);
  };

  const debouncedFilterDepartments = debounce((inputValue, callback) => {
    const url = currentSite
      ? `/organization/sites/${currentSite}/departments`
      : '/organization/departments';
    apiAxios.get(`${url}?page=1&per_page=100&name_cont=${inputValue}`)
      .then(r => callback(buildDepartmentOptions(r.data?.rows || [])))
      .catch(() => callback([]));
  }, 300);

  const loadDepartmentOptions = (inputValue, callback) => {
    debouncedFilterDepartments(inputValue, callback);
  };

  const loadManagerOptions = (inputValue, callback) => {
    // Enriched: the option cards carry full name and email, which is what tells
    // two people with similar three-letter codes apart.
    activeEmployeesEnrichedDebounce(inputValue, callback);
  };

  const redirectEmployeeDetail = (employeeId) => {
    navigate(`/organization/employees/${employeeId}`);
  };

  const handleNewSite = () => {
    setShowNewSiteModal(true);
  };

  const handleNewDepartment = () => {
    setShowNewDepartmentModal(true);
  };

  const handleSiteChange = (selectOptions) => {
    setValue("site_id", selectOptions.value);
    setDepartmentOption(null);
    setCurrentSite(selectOptions.value);
    loadDepartmentOptions('', (filteredOptions) => filteredOptions);
  };

  const filterIndexOptions = async (url, inputValue, isDepartment = false) => {
    try {
      const response = await apiAxios.get(`${url}?page=1&per_page=100&name_cont=${inputValue}`);
      const { rows } = response.data;
      return rows.map((item) => {
        const attr = item.attributes || item;
        return {
          value: attr.id,
          label: isDepartment ? buildDepartmentLabel(attr) : (attr.name || attr.label),
        };
      });
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const positionGroupOptions = positionGroups.map((item) => ({ value: item.id, label: item.name }));

  const fieldRow = (label, children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
      <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-text-right">
        {label}
      </div>
      <div className="tw-col-span-12 sm:tw-col-span-7">
        {children}
      </div>
    </div>
  );

  // Row with no label — content aligned under the field column.
  const offsetRow = (children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-1">
      <div className="tw-col-span-12 sm:tw-col-span-3" />
      <div className="tw-col-span-12 sm:tw-col-span-9">
        {children}
      </div>
    </div>
  );

  const fieldError = (error) => error && (
    <span className="tw-text-red-600 tw-text-xs tw-mt-1 tw-block">{error.message}</span>
  );

  return (
    <>
      <div className={embedded ? 'tw-text-sm' : 'tw-bg-white tw-rounded tw-shadow-sm tw-p-5 tw-text-sm'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* The Drawer supplies its own header and close button. */}
          {!embedded && (
            <div className="tw-flex tw-justify-between tw-items-center tw-border-b tw-border-solid tw-border-slate-200 tw-pb-3">
              <h5 className="tw-mb-0 tw-font-semibold">{title}</h5>
              <IconButton
                icon={<FaArrowLeft size={12} />}
                variant="secondary"
                size="sm"
                onClick={() => navigate(-1)}
              />
            </div>
          )}
          <div className="tw-mt-4">

            {fieldRow('Name', (
              <>
                <input
                  type='text'
                  placeholder='Name'
                  className={INPUT_CLASSES}
                  {...register('name', {
                    required: 'Name must be required.'
                  })}
                />
                {fieldError(errors.name)}
              </>
            ))}

            {fieldRow('First name', (
              <input
                type='text'
                placeholder='First name'
                className={INPUT_CLASSES}
                {...register('first_name', { onChange: syncFullName })}
              />
            ))}

            {fieldRow('Last name', (
              <input
                type='text'
                placeholder='Last name'
                className={INPUT_CLASSES}
                {...register('last_name', { onChange: syncFullName })}
              />
            ))}

            {fieldRow('Full name', (
              <>
                <input
                  type='text'
                  placeholder='Auto-generated from First + Last name'
                  className={`${INPUT_CLASSES} tw-bg-slate-50`}
                  {...register('full_name', {
                    required: 'Full name is required.'
                  })}
                />
                {fieldError(errors.full_name)}
              </>
            ))}

            {fieldRow('Title', (
              <>
                <input
                  type='text'
                  placeholder='Title'
                  className={INPUT_CLASSES}
                  {...register('title', {
                    required: 'Title must be required.',
                    onChange: syncSalesTeam,
                  })}
                />
                {fieldError(errors.title)}
              </>
            ))}

            {fieldRow('Email', (
              <>
                <input
                  type='email'
                  placeholder='Email'
                  className={INPUT_CLASSES}
                  {...register('email', {
                    required: 'Email must be required.'
                  })}
                />
                {fieldError(errors.email)}
              </>
            ))}

            {fieldRow('Phone number', (
              <input
                type='text'
                placeholder='Phone number'
                className={INPUT_CLASSES}
                {...register('phone_number')}
              />
            ))}

            {/* EmployeeSelectField rather than a bare AsyncSelect: it shows avatar,
                code, full name and email per option — what you need to tell two
                people with similar names apart — portals its menu to the body so
                it is not clipped inside a drawer, and seeds its value correctly
                when the record arrives asynchronously.

                It also fixes a real crash: the old onChange read
                `selectedOption.value` unconditionally, so clearing the field threw
                a TypeError. `label` is omitted because fieldRow already draws one. */}
            {fieldRow('Manager', (
              <EmployeeSelectField
                name="parent_id"
                control={control}
                loadOptions={loadManagerOptions}
                defaultValue={defaultManagerOption}
                placeholder="Search by name, code or email…"
                error={errors.parent_id?.message}
              />
            ))}

            {fieldRow('Gender', (
              <Controller
                control={control}
                name='gender'
                defaultValue={false}
                render={({ field }) => (
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <label className="tw-inline-flex tw-items-center tw-gap-1.5 tw-cursor-pointer">
                      <input
                        type='radio'
                        id='gender-male'
                        className="tw-cursor-pointer"
                        checked={!field.value}
                        onChange={() => setValue('gender', false)}
                      />
                      Male
                    </label>
                    <label className="tw-inline-flex tw-items-center tw-gap-1.5 tw-cursor-pointer">
                      <input
                        type='radio'
                        id='gender-female'
                        className="tw-cursor-pointer"
                        checked={!!field.value}
                        onChange={() => setValue('gender', true)}
                      />
                      Female
                    </label>
                  </div>
                )}
              />
            ))}

            {offsetRow(
              <Controller
                control={control}
                name='is_sales'
                defaultValue={false}
                render={({ field }) => (
                  <ToggleSwitch
                    value={!!field.value}
                    onChange={(checked) => setValue('is_sales', checked)}
                    label='Sales team'
                  />
                )}
              />
            )}

            {offsetRow(
              <Controller
                control={control}
                name='active'
                defaultValue={true}
                render={({ field }) => (
                  <ToggleSwitch
                    value={!!field.value}
                    onChange={(checked) => setValue('active', checked)}
                    label='Active'
                  />
                )}
              />
            )}

            {fieldRow('Start date', (
              <Controller
                control={control}
                name="start_date"
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={d => field.onChange(adjustToNoon(d))}
                    className={INPUT_CLASSES}
                    placeholderText="DD-MM-YYYY"
                    dateFormat="dd-MM-yyyy"
                    fixedHeight
                  />
                )}
              />
            ))}

            {fieldRow('Employee Code', (
              <input
                type='text'
                placeholder='Employee Code'
                className={INPUT_CLASSES}
                {...register('hr_code')}
              />
            ))}

            {fieldRow('Birthday', (
              <Controller
                control={control}
                name='birthday'
                render={({ field }) => (
                  <MaskedInput
                    mask={[/\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                    className={INPUT_CLASSES}
                    guide={false}
                    placeholder="DD-MM-YYYY"
                    {...field}
                  />
                )}
              />
            ))}

            {fieldRow('Position group', (
              <Controller
                control={control}
                name='position_group_id'
                render={({ field }) => (
                  <Select
                    {...field}
                    closeMenuOnSelect={true}
                    options={positionGroupOptions}
                    value={positionGroupOptions.find(option => option.value === field.value) || null}
                    placeholder='Select...'
                    classNamePrefix='react-select'
                    onChange={selectedOption => setValue('position_group_id', selectedOption.value)}
                  />
                )}
              />
            ))}

            {fieldRow('Staff policy', (
              <Controller
                control={control}
                name='staff_policy'
                render={({ field }) => (
                  <Select
                    {...field}
                    closeMenuOnSelect={true}
                    options={staffPolicyOptions}
                    value={staffPolicyOptions.find(option => option.value === field.value) || null}
                    placeholder='Select...'
                    classNamePrefix='react-select'
                    onChange={selectedOption => setValue('staff_policy', selectedOption.value)}
                  />
                )}
              />
            ))}

            {fieldRow('Site', (
              <>
                <div className="tw-flex tw-items-center">
                  <Controller
                    control={control}
                    name='site_id'
                    render={() => (
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        defaultValue={defaultSiteOption}
                        loadOptions={loadSiteOptions}
                        closeMenuOnSelect={true}
                        placeholder='Select...'
                        classNamePrefix='react-select'
                        className='tw-w-full'
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={SELECT_MENU_PORTAL}
                        onChange={handleSiteChange}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Site must be required'
                      }
                    }}
                  />
                  <IconButton
                    icon={<FaPlus size={12} />}
                    size="sm"
                    variant="primary"
                    className="tw-ml-2 tw-shrink-0"
                    onClick={handleNewSite}
                  />
                </div>
                {fieldError(errors.site_id)}
              </>
            ))}

            {fieldRow('Deparment', (
              <>
                <div className="tw-flex tw-items-center">
                  <Controller
                    control={control}
                    name='department_id'
                    render={() => (
                      <AsyncSelect
                        key={currentSite}
                        cacheOptions
                        defaultOptions
                        value={departmentOption}
                        loadOptions={loadDepartmentOptions}
                        closeMenuOnSelect={true}
                        placeholder='Select...'
                        classNamePrefix='react-select'
                        className='tw-w-full'
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={SELECT_MENU_PORTAL}
                        onChange={(selectOptions) => {
                          setValue("department_id", selectOptions.value)
                          setDepartmentOption(selectOptions)
                        }}
                      />
                    )}
                  />
                  <IconButton
                    icon={<FaPlus size={12} />}
                    size="sm"
                    variant="primary"
                    className="tw-ml-2 tw-shrink-0"
                    onClick={handleNewDepartment}
                  />
                </div>
                {fieldError(errors.department_id)}
              </>
            ))}

            {!employee && (
              offsetRow(
                <>
                  <Controller
                    control={control}
                    name='create_user_account'
                    defaultValue={false}
                    render={({ field }) => (
                      <ToggleSwitch
                        value={!!field.value}
                        onChange={(checked) => setValue('create_user_account', checked)}
                        label='Create user account'
                      />
                    )}
                  />
                  <span className='tw-text-slate-500 tw-text-[11px] tw-inline-flex tw-items-center tw-gap-1 tw-mt-1'>
                    <FaInfoCircle /> Create a user account to access the Portal system.
                  </span>
                </>
              )
            )}

          </div>
          <div className="tw-bg-slate-50 tw-flex tw-justify-center tw-items-center tw-gap-2 tw-py-3 tw-mt-4 tw-rounded-md">
            <Button type="submit" variant="primary" className="tw-mx-2">Save</Button>
            <Button variant="danger" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </div>

      <NewSiteModal showModal={showNewSiteModal} setshowModal={setShowNewSiteModal} />
      <NewDepartmentModal showModal={showNewDepartmentModal} setshowModal={setShowNewDepartmentModal} />
    </>
  )
};

EmployeeForm.propTypes = {
  title: PropTypes.string.isRequired,
  embedded: PropTypes.bool,
  onSaved: PropTypes.func,
  onCancel: PropTypes.func,
  employee: PropTypes.shape({
    id: PropTypes.number,
    site_id: PropTypes.number,
    site: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    department: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    parent: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    manager: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      full_name: PropTypes.string,
      email: PropTypes.string
    })
  })
};

export default EmployeeForm;
